import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Minus, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useGate } from '@/client/hooks/useGate';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useAppsCatalogAppsQuery,
  useAppsCatalogMetaQuery,
  useAppsCatalogSectionsQuery,
} from '@/client/api/apps-catalog/apps-catalog.query';
import { useReorderAppsCatalogAppsMutation } from '@/client/api/apps-catalog/apps-catalog.mutation';
import type {
  AdminCatalogApp,
  AppCatalogSite,
  AppCatalogStatus,
  AppsCatalogAppsParams,
} from '@/client/types/apps-catalog';
import { AppsCatalogHeader } from '@/client/components/admin/AppsCatalog/AppsCatalogHeader';
import { AppStatusControl } from '@/client/components/admin/AppsCatalog/AppStatusControl';
import {
  APPS_CATALOG_PERMS,
  CatalogIcon,
  StatusPill,
  formatCatalogDate,
  isRoutedKey,
  labelWindowState,
  siteLabel,
  statusLabel,
} from '@/client/components/admin/AppsCatalog/catalogUi';

const SELECT_CLASS = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm';

const LABEL_STATE: Record<'live' | 'scheduled' | 'ended', { text: string; className: string }> = {
  live: { text: 'Live now', className: 'text-green-700' },
  scheduled: { text: 'Scheduled', className: 'text-amber-700' },
  ended: { text: 'Not showing', className: 'text-gray-400' },
};

const Flag = ({ on, label }: { on: boolean; label: string }) => (
  <span
    className={`inline-flex items-center gap-0.5 text-xs ${on ? 'text-gray-700' : 'text-gray-400'}`}
    title={`${label}: ${on ? 'shown' : 'hidden'}`}
  >
    {on ? <Check className="h-3 w-3 text-green-600" /> : <Minus className="h-3 w-3" />}
    {label}
  </span>
);

const MarketingLabelCell = ({ app }: { app: AdminCatalogApp }) => {
  const state = labelWindowState(app);
  if (state === 'none') return <span className="text-sm text-gray-400">—</span>;
  const window =
    app.marketing_label_starts_at || app.marketing_label_ends_at
      ? `From ${formatCatalogDate(app.marketing_label_starts_at)} until ${formatCatalogDate(app.marketing_label_ends_at)}`
      : 'No date window';
  return (
    <div className="space-y-0.5" title={window}>
      <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
        {app.marketing_label}
      </span>
      <span className={`block text-[11px] font-medium ${LABEL_STATE[state].className}`}>
        {LABEL_STATE[state].text}
      </span>
    </div>
  );
};

const HEADINGS = [
  'Name',
  'App key',
  'Section',
  'Status',
  'Sites',
  'Marketing label',
  'Grid / Store',
  'Sort',
  'Actions',
];

const AppsCatalogAppsPage = () => {
  useBreadcrumbs([{ label: 'Apps catalog', href: '/admin/apps-catalog' }, { label: 'Apps' }]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: AppsCatalogAppsParams = {
    section_key: searchParams.get('section_key') ?? '',
    status: (searchParams.get('status') ?? '') as AppCatalogStatus | '',
    site: (searchParams.get('site') ?? '') as AppCatalogSite | '',
  };

  const setFilter = (key: keyof AppsCatalogAppsParams, value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const { data: meta } = useAppsCatalogMetaQuery();
  const { data: sections = [] } = useAppsCatalogSectionsQuery();
  const { data: apps = [], isLoading, isError, error, isFetching } = useAppsCatalogAppsQuery(filters);
  const reorder = useReorderAppsCatalogAppsMutation();
  const writeGate = useGate(APPS_CATALOG_PERMS.write);

  const sectionLabels = useMemo(() => new Map(sections.map((s) => [s.key, s.label])), [sections]);

  // PUT /reorder/apps must list every app in the section, so reordering is
  // offered only when the list is exactly one whole section.
  const reorderable = !!filters.section_key && !filters.status && !filters.site;
  const reorderBusy = reorder.isPending || isFetching || !writeGate.allowed;
  const columnCount = HEADINGS.length + (reorderable ? 1 : 0);

  const move = async (index: number, dir: -1 | 1) => {
    const other = index + dir;
    if (!filters.section_key || other < 0 || other >= apps.length) return;
    const keys = apps.map((app) => app.app_key);
    [keys[index], keys[other]] = [keys[other], keys[index]];
    try {
      await reorder.mutateAsync({ section_key: filters.section_key, app_keys: keys });
      toast.success('Order updated');
    } catch (err) {
      toast.error('Reorder failed', { description: getApiErrorMessage(err, 'Something went wrong.') });
    }
  };

  const hasFilters = !!(filters.section_key || filters.status || filters.site);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <AppsCatalogHeader
          active="apps"
          description="The apps behind ff-ui's header Apps grid and /app/store. Edit copy and placement here; change status from the Status menu, which asks for confirmation."
          actions={
            <PermissionButton
              code={APPS_CATALOG_PERMS.write}
              className="gap-1.5"
              onClick={() => navigate('/admin/apps-catalog/apps/new')}
            >
              <Plus className="h-4 w-4" />
              New app
            </PermissionButton>
          }
        />

        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4 shadow-sm">
          <label className="space-y-1.5">
            <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Section</span>
            <select
              className={SELECT_CLASS}
              value={filters.section_key}
              onChange={(e) => setFilter('section_key', e.target.value)}
            >
              <option value="">All sections</option>
              {sections.map((section) => (
                <option key={section.key} value={section.key}>
                  {section.label}
                  {section.visible ? '' : ' (hidden)'}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Status</span>
            <select
              className={SELECT_CLASS}
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
            >
              <option value="">All statuses</option>
              {(meta?.statuses ?? []).map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Site</span>
            <select
              className={SELECT_CLASS}
              value={filters.site}
              onChange={(e) => setFilter('site', e.target.value)}
            >
              <option value="">All sites</option>
              {(meta?.sites ?? []).map((site) => (
                <option key={site} value={site}>
                  {siteLabel(site)}
                </option>
              ))}
            </select>
          </label>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              Clear filters
            </Button>
          )}
          <p className="ml-auto max-w-sm text-xs text-gray-500">
            {reorderable
              ? 'Use the arrows to reorder apps within this section.'
              : filters.section_key
                ? 'Clear the status and site filters to reorder this section.'
                : 'Filter by one section to reorder its apps.'}
          </p>
        </div>

        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {reorderable && (
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Order
                  </th>
                )}
                {HEADINGS.map((heading) => (
                  <th
                    key={heading}
                    className={`whitespace-nowrap px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      heading === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 6 }).map((_, row) => (
                  <tr key={row}>
                    {Array.from({ length: columnCount }).map((__, cell) => (
                      <td key={cell} className="px-4 py-4">
                        <Skeleton className="h-4 w-20" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-red-600">
                    {getApiErrorMessage(error, 'Could not load catalog apps.')}
                  </td>
                </tr>
              )}

              {!isLoading && !isError && apps.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-gray-500">
                    {hasFilters ? 'No apps match these filters.' : 'No apps in the catalog yet.'}
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                apps.map((app, index) => (
                  <tr key={app.app_key} className="hover:bg-blue-50/40">
                    {reorderable && (
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="flex items-center gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            disabled={index === 0 || reorderBusy}
                            onClick={() => void move(index, -1)}
                            title={writeGate.allowed ? 'Move up' : writeGate.reason}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            disabled={index >= apps.length - 1 || reorderBusy}
                            onClick={() => void move(index, 1)}
                            title={writeGate.allowed ? 'Move down' : writeGate.reason}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/apps-catalog/apps/${encodeURIComponent(app.app_key)}`}
                        className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline"
                      >
                        <CatalogIcon name={app.icon} className="h-4 w-4 flex-shrink-0 text-gray-500" />
                        {app.name}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className="font-mono text-xs text-gray-700">{app.app_key}</span>
                      {meta && !isRoutedKey(meta, app.app_key) && (
                        <span className="block text-[10px] text-amber-700">no ff-ui route</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {sectionLabels.get(app.section_key) ?? app.section_key}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={app.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {app.sites.map(siteLabel).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <MarketingLabelCell app={app} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <Flag on={app.show_in_grid} label="Grid" />
                        <Flag on={app.show_in_store} label="Store" />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-600">
                      {app.sort_order}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" className="gap-1.5" asChild>
                          <Link to={`/admin/apps-catalog/apps/${encodeURIComponent(app.app_key)}`}>
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Link>
                        </Button>
                        <AppStatusControl app={app} meta={meta} />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AppsCatalogAppsPage;
